/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { LbErrorService, NetworkingApiService, NetworkingService } from '../../../../shared';
import { LogService, UtilsService } from '../../../../../../core/services';
import { NetworkInterfaceModel } from 'src/app/modules/loadbalancer/shared/models';
import { Observable } from 'rxjs';
import { ipValidator, nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-edit-interface',
    templateUrl: './edit-interface.component.html',
    styleUrls: ['./edit-interface.component.scss']
})
export class EditInterfaceComponent extends BaseComponent {
    /**
     * FormGroup.
     */
    public formGroup: FormGroup;

    /**
     * Store for dropdown content.
     */
    public dropdown: DropdownModel[] = [
        <DropdownModel>{ key: 'balance-rr', value: 'balance-rr' },
        <DropdownModel>{ key: 'active-backup', value: 'active-backup' },
        <DropdownModel>{ key: '802.3ad', value: '802.3ad' },
    ];

    /**
     * Hide view elements based on variable.
     */
    public isBond: boolean = false;

    /**
     * Store the current bond
     */
    public bond: NetworkInterfaceModel = <NetworkInterfaceModel>{};

    /**
     * Store the edit ID if the bond being edited.
     * @private
     */
    private editId: string;

    /**
     * Store the interfaces for checks against label.
     * @private
     */
    private storedInterfaces: NetworkInterfaceModel[] = [];

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * Stored validator for the view.
     * @protected
     */
    protected readonly ipValidator = ipValidator;

    constructor(
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private networkingApiService: NetworkingApiService,
        private networkingService: NetworkingService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.setupFormGroups();
        this.setUpFormSubscribers();
    }

    /**
     * Submit Interface
     */
    public submit(): Observable<any> {
        return new Observable((observer) => {
            const formValues = this.formGroup.value;
            const { dnsIPv4, dnsIPv6 } = this.networkingService.sortDNS(formValues.dns);
            formValues.dnsIPv4 = dnsIPv4;
            formValues.dnsIPv6 = dnsIPv6;
            delete formValues.dns;

            this.networkingApiService.updateInterfaceById(formValues.id, formValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate(['../../'], { relativeTo: this.route }).then().catch();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.formGroup, error.error.errors);
                        observer.error(error);
                        observer.complete();
                    }

                });
        });
    }

    /**
     * Set up page data ready for editing.
     * @private
     */
    private setUpPageData(): void {
        this.editId = this.route.snapshot.paramMap.get('interfaceId');
        this.networkingApiService.getInterfaces()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: NetworkInterfaceModel[]): void => {
                    const bond: NetworkInterfaceModel = data.find(item => item.id === this.editId);
                    this.storedInterfaces = data;
                    this.isBond = bond.type === 'bond';
                    this.patchInterfaceToForm(bond);
                    this.bond = bond;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditInterfaceComponent:setUpPageData() error', error.message);
                }
            });
    }

    /**
     * Setup Floating IP form.
     * @private
     */
    private setupFormGroups(): void {
        this.formGroup = this.formBuilder.group({
            id: [null],
            name: [null, [Validators.required, nameValidation]],
            type: [null],
            mtu: [null],
            onBoot: [null],
            offload: [null],
            modes: [null],
            dns: new FormArray([new FormControl('', null)]),
        });
    }

    /**
     * Patch interface to form.
     * @param data
     * @private
     */
    private patchInterfaceToForm(data: NetworkInterfaceModel): void {
        const patchValue = {
            id: data.id,
            name: data.name,
            type: data.type,
            mtu: data.settings.mtu,
            onBoot: data.settings.onBoot,
            offload: data.settings.offload,
            modes: data.settings.bondingMode
        };
        this.setUpDNS(data.routing.dnsIPv4.concat(data.routing.dnsIPv6));
        this.formGroup.patchValue(patchValue);
        this.initialFormData = this.formGroup.value;
    }

    /**
     * Set up subject alts.
     * @param dnss
     * @private
     */
    private setUpDNS(dnss: string[]): void {
        const row = this.formGroup.get('dns') as FormArray;
        if (dnss?.length) {
            row.removeAt(0);
            for (const dns of dnss) {
                row.push(new FormControl(dns, [ipValidator]));
            }
        }
    }

    /**
     * Validate the current form data again initial to see if the button should be enabled or disabled.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                            this.formGroup.markAsPristine();
                        } else {
                            this.formGroup.markAsDirty();
                        }
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditInterfaceComponent:setUpFormSubscribers() error', error.message);
                }
            });

        const formGroupName: AbstractControl = this.formGroup.get('name');
        formGroupName.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: name => {
                    this.utilsService.debounce('check-name', () => {
                        this.utilsService.checkDuplicateFormValue(formGroupName, 'name', this.storedInterfaces, name, this.editId);
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditInterfaceComponent:setUpFormSubscribers() error', error.message);
                }
            });
    }
}
